import React, { useState, useEffect, useRef } from "react";
import { Box, Stack, Text, Group } from "@mantine/core";
import confetti from "canvas-confetti";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import duration from "dayjs/plugin/duration";
import timezone from "dayjs/plugin/timezone";

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(duration);
dayjs.extend(timezone);

export type ConfettiType =
  | "default"
  | "fireworks"
  | "snow"
  | "celebration"
  | "burst"
  | "stars"
  | "none";

export interface ContentBlockTypeCountDownProps {
  /** Target date and time in UTC format (e.g., "2026-01-01T12:00:00Z") */
  targetDateTime: string;

  /** Text to display when countdown reaches zero */
  completedText?: string;

  /** Title/label for the countdown */
  title?: string;

  /** Type of confetti effect when countdown completes */
  confettiType?: ConfettiType;

  /** Whether to loop confetti effect */
  loopConfetti?: boolean;

  /** Additional CSS class name for styling */
  className?: string;

  themeConfig?: any;

  /** Callback fired when countdown completes */
  onComplete?: (() => void) | undefined;

  /** Callback fired on each tick */
  onTick?: ((timeRemaining: number) => void) | undefined;
}

interface TimeRemaining {
  years: number;
  months: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalMs: number;
}

export const ContentBlockTypeCountDown: React.FC<
  ContentBlockTypeCountDownProps
> = ({
  targetDateTime,
  completedText = "Time's up!",
  title,
  confettiType = "celebration",
  loopConfetti = false,
  className,
  themeConfig,
  onComplete,
  onTick,
}) => {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining | null>(
    null
  );
  const [isCompleted, setIsCompleted] = useState(false);
  const hasTriggeredComplete = useRef(false);
  const confettiIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Parse target date with dayjs
  const targetDate = dayjs.utc(targetDateTime);

  // Calculate time remaining
  const calculateTimeRemaining = (now: dayjs.Dayjs): TimeRemaining => {
    const diff = targetDate.diff(now);

    if (diff <= 0) {
      return {
        years: 0,
        months: 0,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        totalMs: 0,
      };
    }

    const dur = dayjs.duration(diff);

    // Calculate years and months
    const years = Math.floor(dur.asYears());
    const months = Math.floor(dur.asMonths()) % 12;
    const days = Math.floor(dur.asDays());
    const hours = dur.hours();
    const minutes = dur.minutes();
    const seconds = dur.seconds();

    return {
      years,
      months,
      days,
      hours,
      minutes,
      seconds,
      totalMs: diff,
    };
  };

  // Trigger confetti
  const triggerConfetti = (type: ConfettiType) => {
    if (type === "none") return;

    const canvas = document.createElement("canvas");
    canvas.style.position = "fixed";
    canvas.style.top = "0";
    canvas.style.left = "0";
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.pointerEvents = "none";
    canvas.style.zIndex = "9999";
    document.body.appendChild(canvas);

    const myConfetti = confetti.create(canvas, {
      resize: true,
      useWorker: true,
    });

    const cleanup = () => {
      setTimeout(() => {
        document.body.removeChild(canvas);
      }, 5000);
    };

    switch (type) {
      case "fireworks":
        const duration = 3000;
        const animationEnd = Date.now() + duration;
        const fireworksInterval = setInterval(() => {
          if (Date.now() > animationEnd) {
            clearInterval(fireworksInterval);
            cleanup();
            return;
          }

          myConfetti({
            startVelocity: 30,
            spread: 360,
            ticks: 60,
            zIndex: 0,
            particleCount: 50,
            origin: {
              x: Math.random(),
              y: Math.random() - 0.2,
            },
          });
        }, 250);
        break;

      case "snow":
        const snowDuration = 5000;
        const snowEnd = Date.now() + snowDuration;
        const snowInterval = setInterval(() => {
          if (Date.now() > snowEnd) {
            clearInterval(snowInterval);
            cleanup();
            return;
          }

          myConfetti({
            particleCount: 3,
            angle: 60,
            spread: 55,
            origin: { x: 0, y: 0 },
            colors: ["#ffffff", "#e8f4ff"],
          });
          myConfetti({
            particleCount: 3,
            angle: 120,
            spread: 55,
            origin: { x: 1, y: 0 },
            colors: ["#ffffff", "#e8f4ff"],
          });
        }, 50);
        break;

      case "burst":
        myConfetti({
          particleCount: 150,
          spread: 180,
          origin: { y: 0.6 },
          startVelocity: 45,
        });
        cleanup();
        break;

      case "stars":
        myConfetti({
          particleCount: 100,
          spread: 160,
          origin: { y: 0.6 },
          shapes: ["star"],
          colors: ["#FFD700", "#FFA500", "#FF6347"],
        });
        cleanup();
        break;

      case "celebration":
      default:
        const count = 200;
        const defaults = {
          origin: { y: 0.7 },
        };

        myConfetti({
          ...defaults,
          particleCount: Math.floor(count * 0.25),
          spread: 26,
          startVelocity: 55,
        });

        myConfetti({
          ...defaults,
          particleCount: Math.floor(count * 0.2),
          spread: 60,
        });

        myConfetti({
          ...defaults,
          particleCount: Math.floor(count * 0.35),
          spread: 100,
          decay: 0.91,
          scalar: 0.8,
        });

        myConfetti({
          ...defaults,
          particleCount: Math.floor(count * 0.1),
          spread: 120,
          startVelocity: 25,
          decay: 0.92,
          scalar: 1.2,
        });

        myConfetti({
          ...defaults,
          particleCount: Math.floor(count * 0.1),
          spread: 120,
          startVelocity: 45,
        });

        cleanup();
        break;
    }
  };

  // Update countdown
  useEffect(() => {
    const updateCountdown = () => {
      const now = dayjs.utc();
      const remaining = calculateTimeRemaining(now);

      setTimeRemaining(remaining);
      onTick?.(remaining.totalMs);

      // Check if completed
      if (remaining.totalMs <= 0 && !hasTriggeredComplete.current) {
        setIsCompleted(true);
        hasTriggeredComplete.current = true;
        onComplete?.();

        // Trigger confetti
        if (confettiType !== "none") {
          triggerConfetti(confettiType);

          // Loop confetti if enabled
          if (loopConfetti) {
            confettiIntervalRef.current = setInterval(() => {
              triggerConfetti(confettiType);
            }, 5000);
          }
        }
      }

      // Calculate next update time
      let nextUpdateMs: number;
      if (remaining.totalMs <= 0) {
        return; // Stop updating when completed
      } else if (remaining.days < 1 && remaining.hours < 1) {
        // Within last hour - update every second
        nextUpdateMs = 1000 - now.millisecond();
      } else if (remaining.days < 2) {
        // Within 2 days - update every minute
        nextUpdateMs = (60 - now.second()) * 1000 - now.millisecond();
      } else {
        // More than 2 days - update every hour
        nextUpdateMs =
          (60 - now.minute()) * 60 * 1000 -
          now.second() * 1000 -
          now.millisecond();
      }

      return setTimeout(updateCountdown, nextUpdateMs);
    };

    const timeoutId = updateCountdown();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (confettiIntervalRef.current) {
        clearInterval(confettiIntervalRef.current);
      }
    };
  }, [targetDateTime, confettiType, loopConfetti, onComplete, onTick]);

  // Helper function to pluralize words
  const pluralize = (
    count: number,
    singular: string,
    plural: string
  ): string => {
    return count === 1 ? singular : plural;
  };

  // Format display based on time remaining
  const getDisplayContent = (): { value: string; label: string }[] => {
    if (!timeRemaining || isCompleted) return [];

    const { years, months, days, hours, minutes, seconds } = timeRemaining;

    // More than a year: show years, months, days
    if (years > 0) {
      const remainingDays = days - (years * 365 + months * 30);
      const displayDays = Math.max(0, remainingDays);
      return [
        { value: String(years), label: pluralize(years, "Year", "Years") },
        { value: String(months), label: pluralize(months, "Month", "Months") },
        {
          value: String(displayDays),
          label: pluralize(displayDays, "Day", "Days"),
        },
      ];
    }

    // Within a year but more than 2 days: show days only
    if (days >= 2) {
      return [{ value: String(days), label: pluralize(days, "Day", "Days") }];
    }

    // Within 2 days but more than 1 hour: show days and hours
    if (days >= 1 || hours >= 1) {
      return [
        ...(days > 0
          ? [{ value: String(days), label: pluralize(days, "Day", "Days") }]
          : []),
        { value: String(hours), label: pluralize(hours, "Hour", "Hours") },
        {
          value: String(minutes),
          label: pluralize(minutes, "Minute", "Minutes"),
        },
      ];
    }

    // Within 1 hour: show minutes and seconds
    return [
      {
        value: String(minutes),
        label: pluralize(minutes, "Minute", "Minutes"),
      },
      {
        value: String(seconds),
        label: pluralize(seconds, "Second", "Seconds"),
      },
    ];
  };

  const displayContent = getDisplayContent();

  // Show completed state
  if (isCompleted) {
    return (
      <Box
        className={`content-block-countdown content-block-countdown--completed ${
          className || ""
        }`}
        h="100%"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "var(--mantine-spacing-lg)",
          ...(themeConfig?.contentBlocks?.countdown?.container && {
            ...themeConfig?.contentBlocks?.countdown?.container,
          }),
        }}
      >
        <Stack align="center" gap="md">
          {title && (
            <Text
              className="content-block-countdown__title"
              style={{
                letterSpacing: "0.05em",
                //textTransform: "uppercase",
                ...(themeConfig?.contentBlocks?.countdown?.title && {
                  ...themeConfig?.contentBlocks?.countdown?.title,
                }),
              }}
            >
              {title}
            </Text>
          )}
          <Text
            className="content-block-countdown__completed-text"
            style={{
              textAlign: "center",
              ...(themeConfig?.contentBlocks?.countdown?.onDone && {
                ...themeConfig?.contentBlocks?.countdown?.onDone,
              }),
            }}
          >
            {completedText}
          </Text>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      className={`content-block-countdown ${className || ""}`}
      h="100%"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--mantine-spacing-lg)",
        ...(themeConfig?.contentBlocks?.countdown?.container && {
          ...themeConfig?.contentBlocks?.countdown?.container,
        }),
      }}
    >
      <Stack align="center" gap="md">
        {title && (
          <Text
            className="content-block-countdown__title"
            style={{
              fontSize: "clamp(0.875rem, 2vw, 1.5rem)",
              fontWeight: 600,
              color: "var(--mantine-color-dimmed)",
              fontFamily:
                "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
              textAlign: "center",
              marginBottom: "var(--mantine-spacing-xs)",
              ...(themeConfig?.contentBlocks?.countdown?.title && {
                ...themeConfig?.contentBlocks?.countdown?.title,
              }),
            }}
          >
            {title}
          </Text>
        )}

        <Group gap="xl" justify="center" wrap="wrap">
          {displayContent.map((item, index) => (
            <Stack
              key={index}
              align="center"
              gap={4}
              className="content-block-countdown__unit"
            >
              <Text
                className="content-block-countdown__value"
                style={{
                  letterSpacing: "-0.03em",
                  fontVariantNumeric: "tabular-nums",
                  ...(themeConfig?.contentBlocks?.countdown?.time && {
                    ...themeConfig?.contentBlocks?.countdown?.time,
                  }),
                }}
              >
                {item.value.padStart(2, "0")}
              </Text>
              <Text
                className="content-block-countdown__label"
                style={{
                  textAlign: "center",
                  letterSpacing: "0.1em",
                  ...(themeConfig?.contentBlocks?.countdown?.title && {
                    ...themeConfig?.contentBlocks?.countdown?.title,
                  }),
                }}
              >
                {item.label}
              </Text>
            </Stack>
          ))}
        </Group>
      </Stack>
    </Box>
  );
};
